import { ArrowRight, Mail, Pencil, Phone, Trash2 } from 'lucide-react';
import { formatMobileWithCountryCode } from '@/features/auth/utils/mobileNumber';
import type { TeamMember } from '../api/teamApi';
import {
  getTeamMemberInitials,
  getTeamMemberName,
} from '../utils/teamUtils';

type TeamMemberCardProps = {
  member: TeamMember;
  index?: number;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
};

export function TeamMemberCard({
  member,
  index = 0,
  onView,
  onEdit,
  onDelete,
}: TeamMemberCardProps) {
  const name = getTeamMemberName(member);
  const phone = formatMobileWithCountryCode(
    member.mobileNumber,
    member.mobileCountryCode,
  );

  return (
    <article
      className="camp-fade-up group flex flex-col overflow-hidden rounded-2xl border border-[var(--dash-line)] bg-white shadow-[0_10px_28px_-22px_rgba(16,38,73,0.35)] transition hover:-translate-y-0.5 hover:border-[#C5D4E6] hover:shadow-[0_18px_40px_-24px_rgba(16,38,73,0.4)]"
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      <div className="flex gap-3.5 p-4 pb-3">
        <div
          className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white shadow-sm"
          style={{ background: 'var(--dash-gradient)' }}
        >
          {getTeamMemberInitials(member)}
        </div>
        <div className="min-w-0 flex-1 text-left">
          <div className="mb-1 flex items-start justify-between gap-2">
            <h3 className="m-0 line-clamp-1 text-[15px] font-semibold leading-snug text-[var(--dash-ink)]">
              {name}
            </h3>
          </div>
          <p className="m-0 truncate text-xs text-[var(--dash-muted)]">Sub-account</p>
        </div>
      </div>

      <div className="mx-4 grid grid-cols-1 gap-2 rounded-xl bg-[var(--dash-soft)] p-3 text-left">
        <div className="flex items-center gap-2 text-xs text-[var(--dash-muted)]">
          <Mail className="h-3.5 w-3.5 shrink-0 opacity-70" />
          <span className="truncate text-[var(--dash-ink)]">{member.email || '—'}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-[var(--dash-muted)]">
          <Phone className="h-3.5 w-3.5 shrink-0 opacity-70" />
          <span className="truncate text-[var(--dash-ink)]">{phone || '—'}</span>
        </div>
      </div>

      <div className="mt-auto flex gap-2 p-4 pt-3">
        <button
          type="button"
          onClick={() => onView(member._id)}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[var(--dash-brand)] py-2.5 text-sm font-semibold text-white transition group-hover:brightness-105"
        >
          View
          <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
        </button>
        <button
          type="button"
          onClick={() => onEdit(member._id)}
          className="inline-flex h-[42px] w-[42px] items-center justify-center rounded-xl border border-[var(--dash-line)] text-[var(--dash-muted)] transition hover:border-[var(--dash-brand)] hover:bg-[var(--dash-brand-soft)] hover:text-[var(--dash-brand)]"
          title="Edit team member"
          aria-label="Edit team member"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(member._id)}
          className="inline-flex h-[42px] w-[42px] items-center justify-center rounded-xl border border-[var(--dash-line)] text-[var(--dash-muted)] transition hover:border-red-200 hover:bg-red-50 hover:text-red-500"
          title="Remove team member"
          aria-label="Remove team member"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}
